// apps/pwa-pos/src/lib/migracion-problemas.ts
// Sprint 99 POS.5 — lo que la migración one-shot no pudo mover.
//
// Antes el fallo de la migración terminaba en un `console.error` que nadie ve.
// Como después de POS.5 nada más lee las claves viejas de localStorage, un
// registro que no migra es trabajo del cajero que desaparece de la UI. Estos
// problemas se persisten acá y entran al mismo panel que los problemas de
// sincronización de las colas.
//
// Vive en localStorage y no en IndexedDB a propósito: el caso que hay que poder
// reportar incluye "IndexedDB no está disponible".

export const MIGRACION_PROBLEMAS_KEY = 'mibodega.idb.migracion-problemas';

export interface ProblemaMigracion {
  /** Estable por mensaje: reintentar la migración no duplica la fila. */
  id: string;
  mensaje: string;
  detectadoAt: string;
}

function idDe(mensaje: string): string {
  return `migracion:${mensaje}`;
}

function leerCrudo(): ProblemaMigracion[] {
  try {
    if (typeof localStorage === 'undefined') return [];
    const raw = localStorage.getItem(MIGRACION_PROBLEMAS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is ProblemaMigracion =>
        !!p &&
        typeof p === 'object' &&
        typeof (p as ProblemaMigracion).id === 'string' &&
        typeof (p as ProblemaMigracion).mensaje === 'string',
    );
  } catch {
    return [];
  }
}

function escribir(problemas: ProblemaMigracion[]): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (problemas.length === 0) {
      localStorage.removeItem(MIGRACION_PROBLEMAS_KEY);
      return;
    }
    localStorage.setItem(MIGRACION_PROBLEMAS_KEY, JSON.stringify(problemas));
  } catch {
    // Si no se puede persistir el aviso, el arranque igual tiene que seguir.
  }
}

export function getProblemasMigracion(): ProblemaMigracion[] {
  return leerCrudo();
}

/**
 * Agrega los mensajes que no estaban. Idempotente por mensaje: si el arranque
 * se repite y la migración vuelve a detectar lo mismo, no se acumulan filas.
 */
export function registrarProblemasMigracion(
  mensajes: string[],
  detectadoAt: string = new Date().toISOString(),
): void {
  if (mensajes.length === 0) return;
  const actuales = leerCrudo();
  const conocidos = new Set(actuales.map((p) => p.id));
  const nuevos = mensajes
    .filter((m) => !conocidos.has(idDe(m)))
    .map((m) => ({ id: idDe(m), mensaje: m, detectadoAt }));
  if (nuevos.length === 0) return;
  escribir([...actuales, ...nuevos]);
}

/** El cajero lo vio y lo dio por entendido: no hay reintento posible. */
export function descartarProblemaMigracion(id: string): void {
  const restantes = leerCrudo().filter((p) => p.id !== id);
  escribir(restantes);
}
